import React from "react";
import { Check, RefreshCw, X, Video, ChevronDown, AlertCircle } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import { POSE_VALIDATION } from "../../../constants/interviewInstructions";
import { drawDarkOutline } from "../../../hooks/useProctoringValidation";
import { useLightingCheck } from "../../../hooks/useLightingCheck";
import StepHeader from "./StepHeader";

// Redesigned camera check. Pose validation, stream handling and status
// transitions are unchanged — this renders the existing state in the new
// layout. The overlay canvas is still mounted over the video because
// useProctoringValidation draws the positioning outline into it.
const CameraCheckStep = ({
  isVideoProctoringEnabled,
  noCameraDetected,
  permissionsStatus,
  requestCameraPermission,
  isRequestingCamera,
  errorMessage,
  cameraStatus,
  videoRef,
  overlayCanvasRef,
  outlineImage,
  poseDetector,
  isMediaPipeLoading,
  showHints,
  setShowHints,
  proctoringValidation,
  validationCount,
  getHintForRequirement,
  selectedCamera,
  setSelectedCamera,
  availableCameras,
  handleNextStep,
  onBack,
  stepNumber,
  totalSteps,
  // Normally just advances. When this is the last step of the round it starts
  // it instead, so the caller relabels the button to say so.
  continueLabel = "Continue",
  // Matches the mic half — same step, same header.
  title = "Microphone & Camera",
  description = "Make sure your face is well lit and clearly visible, and that it's quiet enough for your mic to pick up your voice.",
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const isGranted = permissionsStatus.camera === "granted";
  const isVerified = cameraStatus === "verified";
  const isFailed = cameraStatus === "failed";
  const cameraLive = isGranted && !noCameraDetected && !isFailed;

  const cardClass = isLight
    ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm";

  // Live brightness reading off the video frame. Advisory: it is surfaced to
  // the candidate but never gates progression — the pose validator still owns
  // that, exactly as before.
  const lighting = useLightingCheck({ videoRef, enabled: cameraLive });

  const lightingLabel = () => {
    if (lighting.level === "blocked") return "No image";
    if (lighting.level === "low") return "Too dark";
    if (lighting.level === "harsh") return "Too bright";
    return "Checking";
  };

  // A completely black frame almost always means the lens is covered, not that
  // the room is dark — so lead with the shutter rather than the lighting.
  const lightingHint = () => {
    if (lighting.level === "blocked") {
      return "We're not getting any picture. Slide open your camera's privacy shutter and remove anything covering the lens.";
    }
    if (lighting.level === "low") {
      return "Face a window or add a lamp in front of you — avoid lighting from behind.";
    }
    return "Reduce the light behind you, or move away from direct glare.";
  };

  // Only Camera and Lighting are surfaced. Face / hands / posture / distance
  // are still validated by useProctoringValidation and still reported through
  // the hints panel below, unchanged.
  const checklist = [
    { label: "Camera", ok: cameraLive, okText: "On", badText: "Off" },
    {
      label: "Lighting",
      ok: lighting.isGood,
      okText: "Good",
      badText: lightingLabel(),
    },
  ];

  const failingHints = Object.keys(proctoringValidation || {}).filter(
    (key) => !proctoringValidation[key]
  );

  const progressText = () => {
    if (validationCount <= 0) return null;
    if (validationCount < POSE_VALIDATION.TARGET_VALID_FRAMES * 0.3)
      return "Perfect! Hold this position...";
    if (validationCount < POSE_VALIDATION.TARGET_VALID_FRAMES * 0.6)
      return "Great! Keep staying still...";
    if (validationCount < POSE_VALIDATION.TARGET_VALID_FRAMES * 0.8)
      return "Excellent! Almost there...";
    if (validationCount < POSE_VALIDATION.TARGET_VALID_FRAMES)
      return "Amazing! Just a moment more...";
    return "Ready! Processing...";
  };

  const cameraPicker = (availableCameras || []).length > 0 && (
    <div className="relative inline-flex items-center w-full">
      <Video
        className={`w-3.5 h-3.5 absolute left-3.5 pointer-events-none ${
          isLight ? "text-slate-500" : "text-gray-300"
        }`}
        strokeWidth={1.75}
      />
      <select
        data-testid="camera-picker"
        value={selectedCamera || ""}
        onChange={(e) => setSelectedCamera(e.target.value)}
        className={`appearance-none w-full pl-9 pr-9 py-2 rounded-full text-[12px] truncate cursor-pointer ${
          isLight ? "bg-[#F1F2F5] text-slate-600" : "bg-gray-700 text-gray-100"
        }`}
      >
        {(availableCameras || []).map((camera, i) => {
          const label = camera.label || `Camera ${i + 1}`;
          return (
            <option key={camera.deviceId} value={camera.deviceId} title={label}>
              {label.length > 40 ? `${label.slice(0, 40)}…` : label}
            </option>
          );
        })}
      </select>
      <ChevronDown
        className={`w-4 h-4 absolute right-3 pointer-events-none ${
          isLight ? "text-slate-400" : "text-gray-400"
        }`}
        strokeWidth={1.75}
      />
    </div>
  );

  // Preserved verbatim from the previous step: seeds the outline overlay as
  // soon as the video reports its dimensions.
  const handleLoadedMetadata = () => {
    if (!overlayCanvasRef?.current || !videoRef?.current) return;
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    if (!outlineImage) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const canvasAspect = canvas.width / canvas.height;
    const imageAspect = outlineImage.width / outlineImage.height;
    let drawWidth, drawHeight, drawX, drawY;

    if (imageAspect > canvasAspect) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / imageAspect;
      drawX = 0;
      drawY = (canvas.height - drawHeight) / 2;
    } else {
      drawHeight = canvas.height;
      drawWidth = canvas.height * imageAspect;
      drawX = (canvas.width - drawWidth) / 2;
      drawY = 0;
    }

    drawDarkOutline(ctx, outlineImage, drawX, drawY, drawWidth, drawHeight);
  };

  return (
    <div className="w-full">
      <StepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title={title}
        description={description}
      />

      <div className={`mt-7 rounded-2xl border ${cardClass}`}>
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isLight ? "border-[#F1F2F7]" : "border-gray-700/50"
          }`}
        >
          <h2
            className={`text-[13px] font-medium ${
              isLight ? "text-slate-700" : "text-gray-200"
            }`}
          >
            Camera
          </h2>
          <span
            className={`w-[18px] h-[18px] rounded-full flex items-center justify-center ${
              isVerified
                ? "bg-[#22C55E]"
                : isFailed
                ? "bg-red-500"
                : isLight
                ? "border border-[#D8DAE2]"
                : "border border-gray-600"
            }`}
          >
            {isVerified && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            {isFailed && <X className="w-3 h-3 text-white" strokeWidth={3} />}
          </span>
        </div>

        <div className="px-6 py-5">
          {noCameraDetected ? (
            <p className="text-red-400 text-[13px] text-center py-12">
              No camera detected. Please connect a camera and try again.
            </p>
          ) : permissionsStatus.camera === "denied" ? (
            <p className="text-red-400 text-[13px] text-center py-12">
              Camera access is denied. Please enable it in your browser settings
              and refresh the page to continue.
            </p>
          ) : !isGranted ? (
            <div className="flex flex-col items-center gap-4 py-12">
              <p
                className={`text-[13px] text-center ${
                  isLight ? "text-slate-500" : "text-gray-400"
                }`}
              >
                We need access to your camera to run this check.
              </p>
              <button
                onClick={requestCameraPermission}
                disabled={isRequestingCamera}
                className={`px-7 py-2.5 rounded-full text-[14px] font-medium text-white transition-opacity ${
                  isRequestingCamera
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
                }`}
              >
                {isRequestingCamera ? (
                  <span className="flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Requesting…
                  </span>
                ) : (
                  "Grant camera permission"
                )}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-5">
              {/* Preview */}
              <div className="sm:flex-[1.6] min-w-0">
                <div
                  className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
                    isVerified
                      ? "border-[#22C55E]"
                      : isFailed
                      ? "border-red-500"
                      : isLight
                      ? "border-[#E6E7EC]"
                      : "border-gray-600"
                  }`}
                >
                  <div className="relative bg-gray-900 aspect-[16/10]">
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      onLoadedMetadata={
                        isVideoProctoringEnabled ? handleLoadedMetadata : undefined
                      }
                    />

                    {/* Positioning overlay — proctored mode only */}
                    {isVideoProctoringEnabled && (
                      <canvas
                        ref={overlayCanvasRef}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                        style={{ width: "100%", height: "100%" }}
                      />
                    )}

                    {cameraStatus === "checking" &&
                      isVideoProctoringEnabled &&
                      !poseDetector && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center px-3">
                          <div className="text-center">
                            <RefreshCw className="w-6 h-6 text-white animate-spin mx-auto mb-2" />
                            <p className="text-white text-[12px]">
                              {isMediaPipeLoading
                                ? "Loading AI models…"
                                : "Initializing position detection…"}
                            </p>
                            {isMediaPipeLoading && (
                              <p className="text-gray-300 text-[11px] mt-1">
                                This may take a moment on first load
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {/* Result line */}
                <div className="mt-3 min-h-[20px]">
                  {isVerified ? (
                    <p className="flex items-center gap-2 text-[14px] font-medium text-[#22C55E]">
                      <span className="w-4 h-4 rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                      You're all set — good to go.
                    </p>
                  ) : isVideoProctoringEnabled && progressText() ? (
                    <p className="text-[14px] text-[#22C55E] animate-pulse">
                      {progressText()}
                    </p>
                  ) : (
                    <p
                      className={`text-[14px] ${
                        isLight ? "text-slate-400" : "text-gray-400"
                      }`}
                    >
                      {isVideoProctoringEnabled
                        ? "Align your head, shoulders and both hands in frame."
                        : "Checking your camera…"}
                    </p>
                  )}
                </div>

                <p
                  className={`text-[12px] italic leading-relaxed mt-1 ${
                    isLight ? "text-slate-400" : "text-gray-500"
                  }`}
                >
                  Not seeing your camera? Check your browser's site permissions
                  and try again.
                </p>
              </div>

              {/* Device + checks */}
              <div className="sm:flex-1 min-w-0">
                {cameraPicker}

                <ul
                  className={`mt-4 divide-y ${
                    isLight ? "divide-[#F1F2F7]" : "divide-gray-700/50"
                  }`}
                >
                  {checklist.map((row) => (
                    <li
                      key={row.label}
                      className="flex items-center justify-between gap-3 py-2.5"
                    >
                      <span
                        className={`text-[14px] ${
                          isLight ? "text-slate-500" : "text-gray-400"
                        }`}
                      >
                        {row.label}
                      </span>
                      <span
                        className={`flex items-center gap-1.5 text-[12px] font-medium ${
                          row.ok
                            ? "text-[#22C55E]"
                            : isLight
                            ? "text-slate-400"
                            : "text-gray-500"
                        }`}
                      >
                        {row.ok ? (
                          <span className="w-4 h-4 rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
                            <Check
                              className="w-2.5 h-2.5 text-white"
                              strokeWidth={3}
                            />
                          </span>
                        ) : (
                          <span
                            className={`w-4 h-4 rounded-full border shrink-0 ${
                              isLight ? "border-[#D8DAE2]" : "border-gray-600"
                            }`}
                          />
                        )}
                        {row.ok ? row.okText : row.badText}
                      </span>
                    </li>
                  ))}
                </ul>

                {cameraLive && lighting.level && !lighting.isGood && (
                  <p
                    className={`text-[11px] leading-relaxed mt-2 ${
                      isLight ? "text-slate-400" : "text-gray-500"
                    }`}
                  >
                    {lightingHint()}
                  </p>
                )}

                {/* Positioning hints for whatever is still failing */}
                {isVideoProctoringEnabled &&
                  showHints &&
                  failingHints.length > 0 && (
                    <div
                      className={`mt-4 rounded-lg p-3 ${
                        isLight
                          ? "bg-amber-50 border border-amber-200"
                          : "bg-yellow-600/20 border border-yellow-500/30"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span className="text-[11px] font-medium text-amber-600">
                          Need help
                        </span>
                        <button
                          onClick={() => setShowHints(false)}
                          className="ml-auto text-amber-500 hover:text-amber-600"
                          aria-label="Dismiss hints"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="space-y-1">
                        {failingHints.map((requirement) => (
                          <p
                            key={requirement}
                            className={`text-[11px] leading-relaxed ${
                              isLight ? "text-amber-700" : "text-yellow-300"
                            }`}
                          >
                            • {getHintForRequirement(requirement)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {errorMessage && !noCameraDetected && (
            <p className="text-red-400 text-[12px] mt-4 whitespace-pre-line">
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={onBack}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium border transition-colors ${
            isLight
              ? "bg-white border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
              : "bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700/40"
          }`}
        >
          Back
        </button>

        <button
          onClick={handleNextStep}
          disabled={!isVerified}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium transition-opacity ${
            isVerified
              ? "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white hover:opacity-90"
              : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
          }`}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  );
};

export default CameraCheckStep;
